import { Component, Input, OnInit } from '@angular/core';
import { ChartService } from '@app/data/services/chartService';
import { BasicCartesianChartConfig, BasicChartDataSource } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, LineSeriesOption, SeriesOption } from 'echarts';
import { FilterService } from '@app/data/services/filterService';
import { SitesService } from '@app/data/services/sitesService';
import { EnvironmentalDataSource } from '@app/data/services/types/EnvironmentalDataSource';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';
import { buildEchartsConfigFromDataSources } from '../helpers/widgets';
import { groupSeriesResponses } from '@app/widgets/helpers/time-series-aggregation.helper';

/**
 * I've really tried to use types in this file but the inheritence is making it difficult.
 */

@Component({
  selector: 'app-basic-cartesian-chart',
  templateUrl: './basic-cartesian-chart.component.html',
  styleUrls: ['./basic-cartesian-chart.component.scss'],
})
export class BasicCartesianChartComponent extends BaseFilteredEnvironmentalDataChartComponent implements OnInit {
  @Input() config!: BasicCartesianChartConfig;
  @Input() startDateOverride?: string;
  @Input() endDateOverride?: string;
  @Input() chartOptions: EChartsOption | null = null;

  ngOnInit() {
    super.ngOnInit();
    this.id = `basic-cartesian-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';

    // First load.
    this.setWidgetData();
  }

  /**
   * Clear any displayed chart data so the UI does not show stale results while
   * new data is being fetched. This method implements the abstract contract from
   * BaseFilteredEnvironmentalDataChartComponent.
   */
  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  /**
   * Handle updates to selected environmental data sources or dash groups from the FilterService.
   */
  handleSelectionUpdate = ({ filters }: any) => {
    const newSelected = Array.isArray(filters.selectedEnvironmentalDataSources)
      ? filters.selectedEnvironmentalDataSources.slice()
      : [];
    const newGroups = Array.isArray(filters.selectedEnvironmentalDataSourceDashGroups)
      ? filters.selectedEnvironmentalDataSourceDashGroups.slice()
      : [];

    const changed =
      JSON.stringify(newSelected) !== JSON.stringify(this.selectedEnvironmentalDataSources) ||
      JSON.stringify(newGroups) !== JSON.stringify(this.selectedEnvironmentalDataSourceDashGroups);

    if (changed) {
      this.selectedEnvironmentalDataSources = newSelected;
      this.selectedEnvironmentalDataSourceDashGroups = newGroups;
      this.setWidgetData();
    }
  };

  /**
   * Sets the widget data by fetching from the chart service and updating chart options.
   * Will determine applicable data sources based on current selections.
   * @returns
   */
  setWidgetData() {
    this.hasData = false;

    let dateFormat = this.startDate === this.endDate ? 'HH:00' : 'DD/MM/yyyy';
    let newChartOptions: EChartsOption = this.config.echartsConfig;
    const xAxisData: any[] = [];

    this.chartOptions = null;

    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      this.setNoDataChartOptions();
      return;
    }

    // Get the data
    forkJoin(
      applicableDataSources.map((source: any) =>
        this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            this.startDate === this.endDate ? 'hour' : 'day'
          )
          .pipe(take(1))
      )
    ).subscribe({
      next: (queryResponses: any) => {
        const groupBy = this.config?.dataConfig?.groupBy;
        let responsesToUse = queryResponses;
        if (groupBy) {
          responsesToUse = groupSeriesResponses(
            queryResponses,
            applicableDataSources as BasicChartDataSource[],
            groupBy
          );
          if (groupBy.grouping_type === 'time') {
            dateFormat = groupBy.period === 'month' ? 'MMM YYYY' : '[Q]Q YYYY';
          }
        }

        // Sort out the axis.
        // We can't add it to the series yet because it might have missing data points
        // Add the x axis value
        responsesToUse.forEach((seriesData: any) => {
          if (!seriesData || !Array.isArray(seriesData.data)) return;

          const data = seriesData.data;
          if (data.length) this.hasData = true;

          const newDates = data
            .map((dataPoint: any) => dataPoint && dataPoint.date_time)
            .filter((dateTime: any) => !!dateTime && !xAxisData.includes(dateTime));

          if (newDates.length) xAxisData.push(...newDates);
        });
        // Special case - add axis data either side to show we're not just getting one point.
        if (xAxisData.length === 1) {
          if (this.startDate === this.endDate) {
            xAxisData.push(
              moment(xAxisData[xAxisData.length - 1])
                .tz('UTC')
                .add(1, 'hour')
                .format('YYYY-MM-DDTHH:00:00.000\\Z')
            );
            xAxisData.unshift(moment(xAxisData[0]).add(-1, 'hour').tz('UTC').format('YYYY-MM-DDTHH:00:00\\Z'));
          } else {
            xAxisData.push(
              moment(xAxisData[xAxisData.length - 1])
                .tz('UTC')
                .add(1, 'day')
                .format('YYYY-MM-DDT00:00:00.000\\Z')
            );
            xAxisData.unshift(moment(xAxisData[0]).add(-1, 'day').tz('UTC').format('YYYY-MM-DDT00:00:00.000\\Z'));
          }
        }
        // If padding is specified
        if (this.config.dataConfig.addPadding === true) {
          if (this.startDate === this.endDate) {
            xAxisData.push(
              moment(xAxisData[xAxisData.length - 1])
                .tz('UTC')
                .add(1, 'hour')
                .format('YYYY-MM-DDTHH:00:00.000\\Z')
            );
            xAxisData.unshift(moment(xAxisData[0]).add(-1, 'hour').tz('UTC').format('YYYY-MM-DDTHH:00:00.000\\Z'));
            if (this.config.dataConfig.addPaddingAfter === true) {
              xAxisData.push(
                moment(xAxisData[xAxisData.length + 1])
                  .tz('UTC')
                  .add(1, 'hour')
                  .format('YYYY-MM-DDTHH:00:00.000\\Z')
              );
            }
          } else {
            xAxisData.push(
              moment(xAxisData[xAxisData.length - 1])
                .tz('UTC')
                .add(1, 'day')
                .format('YYYY-MM-DDT00:00:00.000\\Z')
            );
            xAxisData.unshift(moment(xAxisData[0]).add(-1, 'day').tz('UTC').format('YYYY-MM-DDT00:00:00.000\\Z'));
            if (this.config.dataConfig.addPaddingAfter === true) {
              xAxisData.push(
                moment(xAxisData[xAxisData.length + 1])
                  .tz('UTC')
                  .add(1, 'day')
                  .format('YYYY-MM-DDT00:00:00.000\\Z')
              );
            }
          }
        }

        if (!this.hasData) {
          this.setNoDataChartOptions();
        } else {
          // Set the data.
          xAxisData.sort((a: string, b: string) => (a > b ? 1 : -1));
          // rebuild newChartOptions from applicableDataSources so series count and names match
          (newChartOptions as any) = buildEchartsConfigFromDataSources(
            newChartOptions,
            applicableDataSources,
            this.allEnvironmentalDataSources,
            'line',
            this.config.dataConfig.displayLabelOptions
          );

          responsesToUse.forEach((seriesData: any, index: number) => {
            const dataSource: BasicChartDataSource = applicableDataSources[index];

            if (!seriesData || !Array.isArray(seriesData.data)) {
              (newChartOptions.series as SeriesOption[])[index].data = xAxisData.map(() => null);
              return;
            }

            // Key the data by date_time
            const sigFigs =
              seriesData && seriesData.analytes && seriesData.analytes[0]
                ? seriesData.analytes[0].significant_figures
                : undefined;

            let keyedData = seriesData.data.reduce((accumulator: any, dataPoint: any) => {
              if (!dataPoint || !dataPoint.date_time) return accumulator;
              const rawVal = dataPoint[dataSource.valueField];
              let formattedVal: any = rawVal;
              if (rawVal != null && typeof rawVal === 'number' && sigFigs) {
                try {
                  formattedVal = rawVal.toPrecision(sigFigs);
                } catch (e) {
                  formattedVal = rawVal;
                }
              }
              return {
                [dataPoint.date_time]: formattedVal,
                ...accumulator,
              };
            }, {});

            // If there's a post processing function, run it now
            if (this.config.dataConfig.postProcessingFunc) {
              keyedData = this.config.dataConfig.postProcessingFunc(keyedData);
            }

            // Special case - stacked graphs with a single datapoint
            // Add the data to the series
            (newChartOptions.series as SeriesOption[])[index].data = xAxisData.map(
              (dateTimeValue: string) =>
                keyedData[dateTimeValue] ||
                // The special case
                ((newChartOptions.series as SeriesOption[])[index].type === 'line' &&
                (newChartOptions.series as LineSeriesOption[])[index].stack === 'Total' &&
                seriesData.data.length < xAxisData.length
                  ? 0
                  : null)
            );
          });

          const formatAsDate = this.config.dataConfig.formatXAxisAsDate !== false;
          newChartOptions.xAxis = {
            ...(newChartOptions.xAxis || {}),
            type: 'category',
            boundaryGap: false,
            data: xAxisData.map((date: string, index: number) =>
              this.config.dataConfig.addPadding && (index === 0 || index === xAxisData.length - 1)
                ? ''
                : formatAsDate
                ? moment(date).format(dateFormat)
                : date
            ),
          };

          this.chartOptions = newChartOptions;
        }
      },
      error: (err: any) => {
        console.error(err);
        this.setNoDataChartOptions();
      },
    });
  }

  /**
   * Set a default "no data" chart options payload.
   * @param message Optional title text to show on the chart.
   */
  private setNoDataChartOptions(): void {
    const text = 'No data is available for the selected date range.';
    this.chartOptions = {
      color: ['#6AB6B0'],
      title: {
        text,
      },
    };
  }
}
